import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColumns,
  GridRowId,
} from "@mui/x-data-grid";
import { FaTrash, FaTimes } from "react-icons/fa";
import {
  getPointers,
  insertPointer,
  removePointer,
} from "../../../../../server/server";
import { notif } from "../../../../utils/util";
import ConfirmationModal from "../../../../modals/ConfirmationModal";

interface PointersModalProps {
  open: boolean;
  onClose: () => void;
  item: any; // measure object
  disabled?: boolean;
}

const PointersModal: React.FC<PointersModalProps> = ({
  open,
  onClose,
  item,
  disabled = false,
}) => {
  const [title, setTitle] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [rowCountState, setRowCountState] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<any>();
  const [deadline, setDeadline] = useState<string>("");
  const [defaultInstitution, setDefaultInstitution] = useState<any>();
  const [comment, setComment] = useState("");
  const [selectedOption, setSelectedOption] = useState(0);

  const loadPointers = async (offset: any = 0) => {
    if (!item?.id) return;
    setIsLoading(true);
    const resp = await getPointers(item.measureId);
    if (resp?.status === 200) {
      setItems(resp?.data || []);
      setIsLoading(false);
    } else {
      setItems([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && item?.id) {
      loadPointers();
      // reset form
      setTitle("");
    }
  }, [open, item?.id]);

  const insertNew = async () => {
    if (!item?.id || !title?.trim()) {
      notif("Унесите показатељ", "error");
      return;
    }
    const resp = await insertPointer({
      text: title,
      measure: String(item.id),
      deadline,
      institution: defaultInstitution?.id || "",
      comment,
      status: selectedOption?.toString(),
    });

    if (resp?.status === 201) {
      notif("Показатељ успешно додат", "success");
      setTitle("");
      loadPointers();
    } else {
      notif("Грешка при додавању показатеља", "error");
    }
  };

  const delPointer = async () => {
    if (!deleteId) return;
    const resp = await removePointer(deleteId);
    if (resp?.status === 200) {
      notif("Показатељ успешно обрисан", "success");
      setItems((prev) => prev.filter((r) => r.id !== deleteId));
    } else {
      notif("Показатељ није обрисан", "error");
    }
  };

  const deletePointer = () => {
    delPointer();
    setDeleteModalIsOpen(false);
  };

  const deleteItem = React.useCallback(
    (id: GridRowId) => () => {
      setTimeout(() => {
        setDeleteModalIsOpen(true);
        setDeleteId(id);
      });
    },
    []
  );

  const columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        field: "text",
        headerName: "Показатељ",
        flex: 1,
        renderCell: (params: any) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              lineHeight: "1.5",
              padding: "8px 0",
            }}
          >
            {params.value}
          </div>
        ),
      },
      // {
      //   field: "actions",
      //   type: "actions",
      //   width: 60,
      //   getActions: (params: any) => [
      //     <GridActionsCellItem
      //       icon={<FaTrash style={{ width: 20, height: 20 }} />}
      //       sx={{ width: 50, height: 50 }}
      //       label="Обриши"
      //       onClick={deleteItem(params.id)}
      //       disabled={disabled}
      //     />,
      //   ],
      // },
    ],
    [deleteItem, disabled]
  );

  const handleClose = () => {
    onClose();
    setItems([]);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>
          <div className="flex justify-between items-center">
            <span className="text-[#04438b] text-2xl font-bold">
              Показатељи - {item?.measureName}
            </span>
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="p-4 w-full flex flex-col gap-4">
            {/* <div className="flex items-center gap-6">
              <div className="flex flex-col items-start gap-3 w-full">
                <p className="w-[260px]">Показатељ</p>
                <TextField
                  disabled={disabled}
                  label="Показатељ"
                  size="small"
                  multiline
                  sx={{ width: "100%", height: "100%" }}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            </div>

            <div className="w-full flex justify-end mt-2 items-center">
              <Button
                disabled={disabled || !title.trim()} // <-- onemogući dok nema teksta
                variant="contained"
                sx={{ backgroundColor: "#0F2765" }}
                onClick={insertNew}
              >
                Потврди
              </Button>
            </div> */}

            <div className="mt-5 h-[350px] w-full">
              <DataGrid
                rowCount={rowCountState}
                loading={isLoading}
                pagination
                disableColumnMenu
                page={page}
                pageSize={pageSize}
                rows={items}
                columns={columns}
                getRowHeight={() => "auto"}
                style={{ zIndex: 0 }}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                components={{
                  NoRowsOverlay: () => (
                    <Stack
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                    >
                      Не постоје показатељи
                    </Stack>
                  ),
                  NoResultsOverlay: () => (
                    <Stack
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                    >
                      Не постоје показатељи
                    </Stack>
                  ),
                }}
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <div
            className="border border-[#81848f] hover:bg-[#81848f] hover:text-white cursor-pointer flex items-center justify-center text-base font-semibold rounded-lg w-[150px] h-[55px] mr-4"
            onClick={handleClose}
          >
            Затвори
          </div>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        modalIsOpen={deleteModalIsOpen}
        closeModal={() => setDeleteModalIsOpen(false)}
        confirmation={deletePointer}
        message={`Обриши показатељ?`}
      />
    </>
  );
};

export default PointersModal;
